import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import { ChevronLeft, FileText, CheckCircle2, Clock, XCircle, AlertTriangle } from "lucide-react";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

export default function W9Application() {
  const navigate = useNavigate();
  const safeBack = () => window.history.length > 2 ? navigate(-1) : navigate("/referrals", { replace: true });
  const [loading, setLoading] = useState(true);
  const [w9Status, setW9Status] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    legal_name: "",
    tax_id_last4: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    signature_confirmed: false,
  });

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const r = await api.get("/referrals/w9-status");
      setW9Status(r.data);
    } catch (e) {
      toast.error("Failed to load W-9 status");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.signature_confirmed) {
      toast.error("You must confirm the accuracy of your information");
      return;
    }
    if (form.tax_id_last4.length !== 4 || !/^\d{4}$/.test(form.tax_id_last4)) {
      toast.error("Please enter exactly 4 digits for your SSN/EIN");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/referrals/w9-submit", form);
      toast.success(r.data.message || "W-9 submitted successfully");
      setW9Status({ status: "pending", submitted_at: new Date().toISOString() });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit W-9");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#09090B] text-zinc-500 flex items-center justify-center label-eyebrow">LOADING...</div>;
  }

  const statusDisplay = () => {
    if (!w9Status || w9Status.status === "none") return null;

    const configs = {
      pending: { icon: Clock, color: "text-amber-500", border: "border-amber-500/20", bg: "bg-amber-500/5", label: "Under Review", desc: "Your W-9 is being reviewed. This typically takes 1-2 business days." },
      approved: { icon: CheckCircle2, color: "text-emerald-500", border: "border-emerald-500/20", bg: "bg-emerald-500/5", label: "Approved", desc: "Your W-9 has been approved. You are eligible for payouts." },
      rejected: { icon: XCircle, color: "text-red-500", border: "border-red-500/20", bg: "bg-red-500/5", label: "Rejected", desc: w9Status.rejection_reason || "Your W-9 was rejected. Please resubmit with corrected information." },
    };
    const cfg = configs[w9Status.status] || configs.pending;
    const Icon = cfg.icon;

    return (
      <div className={`border ${cfg.border} ${cfg.bg} p-6 mb-8`}>
        <div className="flex items-center gap-3 mb-2">
          <Icon size={20} className={cfg.color} />
          <span className={`text-sm font-bold uppercase tracking-widest ${cfg.color}`}>{cfg.label}</span>
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed">{cfg.desc}</p>
        {w9Status.submitted_at && (
          <p className="text-[10px] text-zinc-600 mt-2 font-mono">
            Submitted: {new Date(w9Status.submitted_at).toLocaleDateString()}
          </p>
        )}
      </div>
    );
  };

  const showForm = !w9Status || w9Status.status === "none" || w9Status.status === "rejected";

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar />
      <div className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={safeBack} className="flex items-center gap-2 text-zinc-400 hover:text-white">
            <ChevronLeft size={16} />
            <span className="text-xs font-bold uppercase tracking-widest">Back to Referrals</span>
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <FileText size={20} className="text-zinc-400" />
          <h1 className="text-lg font-black uppercase tracking-widest">W-9 Tax Form</h1>
        </div>
        <p className="text-xs text-zinc-500 mb-8 leading-relaxed">
          Required by the IRS before referral payouts can be processed. Your information is encrypted and stored securely.
        </p>

        {statusDisplay()}

        {showForm && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
                Legal Name (as shown on tax return)
              </label>
              <input
                type="text"
                value={form.legal_name}
                onChange={(e) => handleChange("legal_name", e.target.value)}
                required
                minLength={2}
                maxLength={120}
                className="w-full bg-[#121214] border border-white/10 px-4 py-3 text-sm focus:border-white/30 focus:outline-none transition-colors"
                placeholder="John Doe or Business Name LLC"
              />
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
                Last 4 Digits of SSN or EIN
              </label>
              <input
                type="text"
                value={form.tax_id_last4}
                onChange={(e) => handleChange("tax_id_last4", e.target.value.replace(/\D/g, "").slice(0, 4))}
                required
                maxLength={4}
                pattern="\d{4}"
                className="w-full bg-[#121214] border border-white/10 px-4 py-3 text-sm font-mono focus:border-white/30 focus:outline-none transition-colors"
                placeholder="••••"
              />
              <p className="text-[10px] text-zinc-600 mt-1">We only store the last 4 digits for verification.</p>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
                Address Line 1
              </label>
              <input
                type="text"
                value={form.address_line1}
                onChange={(e) => handleChange("address_line1", e.target.value)}
                required
                minLength={5}
                maxLength={200}
                className="w-full bg-[#121214] border border-white/10 px-4 py-3 text-sm focus:border-white/30 focus:outline-none transition-colors"
                placeholder="123 Main Street"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
                Address Line 2 <span className="text-zinc-700">(Optional)</span>
              </label>
              <input
                type="text"
                value={form.address_line2}
                onChange={(e) => handleChange("address_line2", e.target.value)}
                maxLength={200}
                className="w-full bg-[#121214] border border-white/10 px-4 py-3 text-sm focus:border-white/30 focus:outline-none transition-colors"
                placeholder="Suite 100"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="col-span-2 sm:col-span-2">
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">City</label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => handleChange("city", e.target.value)}
                  required
                  minLength={2}
                  maxLength={100}
                  className="w-full bg-[#121214] border border-white/10 px-4 py-3 text-sm focus:border-white/30 focus:outline-none transition-colors"
                  placeholder="Los Angeles"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">State</label>
                <select
                  value={form.state}
                  onChange={(e) => handleChange("state", e.target.value)}
                  required
                  className="w-full bg-[#121214] border border-white/10 px-4 py-3 text-sm focus:border-white/30 focus:outline-none transition-colors"
                >
                  <option value="">--</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">ZIP</label>
                <input
                  type="text"
                  value={form.zip_code}
                  onChange={(e) => handleChange("zip_code", e.target.value.replace(/[^\d-]/g, "").slice(0, 10))}
                  required
                  minLength={5}
                  maxLength={10}
                  className="w-full bg-[#121214] border border-white/10 px-4 py-3 text-sm font-mono focus:border-white/30 focus:outline-none transition-colors"
                  placeholder="90001"
                />
              </div>
            </div>

            <div className="border border-white/10 bg-[#121214] p-6 mt-8">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Under penalties of perjury, I certify that the information provided is correct. 
                  This serves as my electronic signature for tax reporting purposes.
                </p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.signature_confirmed}
                  onChange={(e) => handleChange("signature_confirmed", e.target.checked)}
                  className="w-4 h-4 rounded border-zinc-600 bg-zinc-900 text-emerald-500 focus:ring-emerald-500/20"
                />
                <span className="text-xs font-bold">I confirm this information is accurate under penalty of perjury</span>
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting || !form.signature_confirmed}
              className={`w-full py-4 text-sm font-black uppercase tracking-widest transition-all ${
                form.signature_confirmed
                  ? "bg-white text-black hover:bg-zinc-200"
                  : "bg-zinc-800 text-zinc-600 cursor-not-allowed"
              }`}
            >
              {submitting ? "SUBMITTING..." : "SUBMIT W-9"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
